import PropTypes from "prop-types";
import { useMemo, useState } from "react";

import {
  useBudgetPlan,
  useUpdateBudgetPlan,
} from "@features/budgeting-game/api";
import {
  CustomGoalsContainer,
  GoalsContainer,
} from "@features/goals/components";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  ErrorToast,
  Loader,
} from "@features/ui";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { useCareerPlan } from "@features/career-details/api/use-career-plan";
import { useCareerGoals } from "@features/goals/api/use-career-goals.js";
import { useCreateCareerPlanGoal } from "@features/goals/api/use-create-career-plan-goal";
import { useSubmitCareerPlan } from "@features/goals/api/use-submit-career-plan";
import { StartOverConfirmationModal } from "@features/shared";
import { useOrganizationSettings } from "@hooks/use-organization-settings";
import { useSyncState } from "@hooks/use-sync-state";
import { toCapitalized } from "@lib/text.helpers";
import { buttonTracking } from "@lib/tracking.helpers";
import { useStartOver } from "@pages/journey-map/api/use-start-over";
import { useUserJourney } from "@pages/journey-map/api/use-user-journey";

import { useBudgetGoals } from "./api/use-budget-goals";
import { useCreateBudgetPlanGoal } from "./api/use-create-budget-plan-goal";

import "./index.scss";

export const Goals = ({ onClose, onRemovePlan, onSubmit }) => {
  const [openConfirmationModal, setOpenConfirmationModal] = useState();

  const [customGoal, setCustomGoal] = useSyncState("customGoal");

  const { data } = useOrganizationSettings();
  const advisorRole = toCapitalized(data?.advisorRole ?? "counselor");

  const onCareerPlanGoalCreated = (_, submittedGoal) => {
    if (submittedGoal.type === "custom") {
      setCustomGoal();
    }
  };

  const {
    data: {
      id: budgetPlanId,
      draftGoals: budgetPlanDraftGoals = [],
      goals: budgetPlanGoals,
      submittedAt: budgetPlanSubmittedAt,
    },
  } = useBudgetPlan();
  const {
    data: {
      id: careerPlanId,
      careerId,
      draftGoals: careerPlanDraftGoals = {},
      goals: careerPlanGoals,
      career,
      submittedAt: careerPlanSubmittedAt,
    },
  } = useCareerPlan();

  const { isFetching } = useUserJourney();

  const { isStartingOver, startOver } = useStartOver();

  const { data: careerGoalsByType, isError: isCareerGoalsByTypeError } =
    useCareerGoals(careerId);
  const { isPending: submittingCareerGoal, mutateAsync: submitCareerGoal } =
    useCreateCareerPlanGoal(careerPlanId, onCareerPlanGoalCreated);
  const {
    isPending: submittingCareerPlan,
    mutateAsync: submitCareerPlan,
    isError: submitCareerPlanError,
  } = useSubmitCareerPlan(careerPlanId);

  const { data: budgetGoals, isError: isBudgetGoalsError } = useBudgetGoals();
  const {
    isPending: submittingBudgetGoal,
    mutateAsync: submitBudgetGoal,
    isError: isSubmitBudgetGoalError,
  } = useCreateBudgetPlanGoal(budgetPlanId);
  const {
    isPending: submittingBudgetPlan,
    mutateAsync: updateBudgetPlan,
    isError: submitBudgetPlanError,
  } = useUpdateBudgetPlan(budgetPlanId);

  const doSubmitCareerPlan = async () => {
    await Promise.allSettled([
      submitCareerPlan(),
      updateBudgetPlan({
        submit: true,
      }),
    ]);
    onSubmit?.();
  };

  const submitOnBlur = async () => {
    if (customGoal) {
      await submitCareerGoal(customGoal);
    }
  };

  const submitButtonProps = useMemo(() => {
    const requirementsMet =
      budgetPlanDraftGoals?.length > 0 &&
      careerPlanDraftGoals.short?.length > 0 &&
      careerPlanDraftGoals.long?.length > 0;
    let enabled = requirementsMet;

    if (requirementsMet && careerPlanSubmittedAt) {
      // The plan was previously submitted and the requirements are still met.

      // Has the number of goals changed?
      const numGoalsTheSame =
        budgetPlanDraftGoals.length === budgetPlanGoals.length &&
        careerPlanDraftGoals.short.length === careerPlanGoals.short.length &&
        careerPlanDraftGoals.long.length === careerPlanGoals.long.length &&
        careerPlanDraftGoals.custom?.length === careerPlanGoals.custom?.length;

      if (numGoalsTheSame) {
        // Were any of the draft goals created after the submission date?
        const parsedBudgetPlanSubmittedAt = Date.parse(budgetPlanSubmittedAt);
        const parsedCareerPlanSubmittedAt = Date.parse(careerPlanSubmittedAt);
        enabled =
          budgetPlanDraftGoals.some(
            (_draftGoal) =>
              Date.parse(_draftGoal.createdAt) > parsedBudgetPlanSubmittedAt,
          ) ||
          Object.values(careerPlanDraftGoals).some((_draftGoals) =>
            _draftGoals.some(
              (draftGoal) =>
                Date.parse(draftGoal.createdAt) > parsedCareerPlanSubmittedAt,
            ),
          );
      }
    }

    let label = `Send to ${advisorRole}`;
    if (enabled) {
      label = careerPlanSubmittedAt
        ? `Resend to ${advisorRole}`
        : `Send to ${advisorRole}`;
    } else if (requirementsMet) {
      label = "Submitted";
    }

    return { enabled, label };
  }, [
    budgetPlanDraftGoals,
    budgetPlanGoals,
    careerPlanDraftGoals,
    careerPlanGoals,
    careerPlanSubmittedAt,
    advisorRole,
  ]);

  const onBudgetGoalSelected = async (_, goal, orderNum) => {
    if (!budgetPlanId) {
      console.warn("Cannot select budget goal without budget plan");
      return;
    }
    const existingGoal = budgetPlanDraftGoals?.find(
      (budgetPlanGoal) => budgetPlanGoal.orderNum === orderNum,
    );

    if (!existingGoal || !goal || existingGoal.budgetGoalId !== goal.id) {
      try {
        await submitBudgetGoal({
          id: existingGoal?.id,
          budgetPlanId,
          budgetGoalId: goal?.id,
          orderNum,
        });
      } catch (error) {
        console.error("onBudgetGoalSelected failed", error);
      }
    }
  };

  const onCareerGoalSelected = async (type, goal, orderNum) => {
    const existingGoal = careerPlanDraftGoals[type]?.find(
      (careerPlanGoal) => careerPlanGoal.orderNum === orderNum,
    );
    if (!existingGoal || !goal || existingGoal.careerGoalId !== goal.id) {
      await submitCareerGoal({
        type,
        id: existingGoal?.id,
        careerPlanId,
        careerGoalId: goal?.id,
        orderNum,
      });
    }
  };

  const onCustomGoalChanged = (goalText, orderNum) => {
    if (goalText !== "" || orderNum in careerPlanDraftGoals.custom) {
      setCustomGoal({
        type: "custom",
        id: careerPlanDraftGoals.custom?.[orderNum].id,
        careerPlanId,
        goalText,
        orderNum,
      });
    }
  };

  const closeConfirmationModal = () => setOpenConfirmationModal();

  const onConfirmStartOver = async () => {
    await startOver();
    closeConfirmationModal();
    onRemovePlan?.();
  };

  const getLoaderMessage = () => {
    if (submittingCareerPlan || submittingBudgetPlan) {
      return "submiting goals...";
    } else if (submittingCareerGoal || submittingBudgetGoal) {
      return "saving goal...";
    } else if (isStartingOver) {
      return "removing pathway and goals...";
    }
  };

  return (
    <>
      <Card className="goals-card">
        <CardHeader
          className="goals-card-header"
          title={<h3>My Goals for...</h3>}
          toolbar={
            <Button
              onClick={onClose}
              icon={["fa-regular", "xmark"]}
              title="close"
              {...buttonTracking("goals", "close")}
            />
          }
        >
          <h1>{career?.title}</h1>
        </CardHeader>
        <CardBody className="goals-card-body">
          <ErrorToast
            open={
              submitCareerPlanError ||
              submitBudgetPlanError ||
              isCareerGoalsByTypeError ||
              isBudgetGoalsError ||
              isSubmitBudgetGoalError
            }
          />
          <div className="goals-instructions">
            Select at least one goal from each category.
          </div>
          <GoalsContainer
            type="budget"
            goals={budgetPlanId ? budgetGoals : []}
            budgetPlanGoals={budgetPlanId ? budgetPlanDraftGoals : []}
            onGoalSelected={budgetPlanId ? onBudgetGoalSelected : () => {}}
          />
          <GoalsContainer
            type="short"
            goals={careerGoalsByType?.short}
            careerPlanGoals={careerPlanDraftGoals?.short}
            onGoalSelected={onCareerGoalSelected}
          />
          <GoalsContainer
            type="long"
            goals={careerGoalsByType?.long}
            careerPlanGoals={careerPlanDraftGoals?.long}
            onGoalSelected={onCareerGoalSelected}
          />
          <CustomGoalsContainer
            selectedGoals={careerPlanDraftGoals?.custom}
            onBlur={submitOnBlur}
            onGoalTextChanged={onCustomGoalChanged}
          />
        </CardBody>
        <CardFooter className="goals-card-footer">
          <div className="actions-container">
            <Button
              primary
              icon={["fa-regular", "paper-plane"]}
              onClick={doSubmitCareerPlan}
              disabled={!submitButtonProps.enabled}
              {...buttonTracking("goals", "submit")}
            >
              {submitButtonProps.label}
            </Button>
            <Button
              icon={["fa-regular", "arrow-rotate-left"]}
              onClick={() => setOpenConfirmationModal(true)}
              {...buttonTracking("goals", "start-over")}
            >
              Start Over
            </Button>
          </div>
        </CardFooter>
      </Card>
      <Loader
        show={
          submittingCareerPlan ||
          submittingCareerGoal ||
          submittingBudgetGoal ||
          isStartingOver ||
          isFetching
        }
        text={getLoaderMessage()}
      />
      <StartOverConfirmationModal
        open={openConfirmationModal}
        onConfirm={onConfirmStartOver}
        onCancel={closeConfirmationModal}
        onClose={closeConfirmationModal}
        career={career}
        trackingPrefix="goals"
      />
    </>
  );
};

Goals.displayName = "Goals Card";

Goals.propTypes = {
  onClose: PropTypes.func,
  onRemovePlan: PropTypes.func,
  onSubmit: PropTypes.func,
};
